import { useState } from 'react';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import FormFieldset from 'components/forms/form/FormFieldset';
import { ModalBody } from 'react-bootstrap';
import useFetchCustomTemplateByUid from 'components/domain/automation/customTemplates/useFetchCustomTemplateByUid';
import FormWithProvider from 'components/forms/form/Form';
import { MessagingType } from 'models/automation/CustomTemplates';
import { UsageType } from 'models/automation/Common';
import { useSearchParams } from 'react-router-dom';
import { LanguageCode } from 'i18n/internationalization.types';
import useAvailableLanguages from 'pages/automations/common/useAvailableLanguages';
import { TRANSLATE_CUSTOM_TEMPLATE_MUTATION_KEY } from 'components/domain/automation/customTemplates/useTranslateCustomTemplateMutation';
import { useIsMutating } from '@tanstack/react-query';
import {
  customTemplateAddEditFormSchema,
  getFormDefaultValues,
} from './CustomTemplateAddEditModal.schema';
import CustomTemplateAddEditModalFormFields from './CustomTemplateAddEditModalFormFields';
import useCustomTemplateFormSubmit from './useCustomTemplateFormSubmit';
import CustomTemplateAddEditModalFooter from './CustomTemplateAddEditModalFooter';
import { TEMPLATE_TYPE_QUERY_PARAM } from './CustomTemplateAddEditModal.constants';

const CustomTemplateAddEditModal = ({
  customTemplateUid,
  closeModal,
}: {
  customTemplateUid?: string;
  closeModal: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const isEditMode = !!customTemplateUid;
  const { availableLanguages, isLoading: isLoadingAvailableLanguages } =
    useAvailableLanguages(UsageType.CUSTOM);
  const { data: customTemplate, isLoading: isLoadingCustomTemplate } =
    useFetchCustomTemplateByUid({
      customTemplateUid,
      enabled: isEditMode,
    });
  const isTranslating =
    useIsMutating({
      mutationKey: [TRANSLATE_CUSTOM_TEMPLATE_MUTATION_KEY],
    }) > 0;
  const { handleSubmit } = useCustomTemplateFormSubmit();
  // undefined means the user hasn't toggled the checkbox yet — fall back to the
  // template's persisted value (or false for new templates).
  const [autoTranslateUserChoice, setAutoTranslateUserChoice] = useState<
    boolean | undefined
  >(undefined);
  const autoTranslateState =
    autoTranslateUserChoice ?? customTemplate?.autoTranslate ?? false;

  if (
    (isLoadingCustomTemplate && customTemplateUid) ||
    isLoadingAvailableLanguages
  ) {
    return <LoadingPlaceholder />;
  }

  const templateTypeParam = searchParams.get(TEMPLATE_TYPE_QUERY_PARAM);
  const messagingType =
    customTemplate?.messagingType ?? (templateTypeParam as MessagingType);

  return (
    <FormWithProvider
      horizontal
      defaultValues={getFormDefaultValues({
        customTemplate,
        availableLanguages,
        templateType: templateTypeParam as MessagingType,
        currentLanguage: LanguageCode.EN,
        autoTranslate: autoTranslateState,
      })}
      onSubmit={handleSubmit}
      schema={customTemplateAddEditFormSchema({
        availableLanguages,
        messagingType,
      })}
      dirtyChecker
      dirtyCheckerOptions={{
        skipFields: ['language'],
      }}
      noValidate
      key={`${templateTypeParam}`}
    >
      <FormFieldset disabled={isTranslating}>
        <ModalBody>
          <CustomTemplateAddEditModalFormFields
            availableLanguages={availableLanguages}
            onChangeAutoTranslate={setAutoTranslateUserChoice}
            isEditMode={isEditMode}
          />
        </ModalBody>
        <CustomTemplateAddEditModalFooter
          closeModal={closeModal}
          customTemplate={customTemplate}
        />
      </FormFieldset>
    </FormWithProvider>
  );
};

CustomTemplateAddEditModal.defaultProps = {
  customTemplateUid: undefined,
};

export default CustomTemplateAddEditModal;
